import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronUp, Linkedin } from "lucide-react";
import logoCream from "@/assets/logo-cream.png.asset.json";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { useAuth } from "@/hooks/useAuth";

const LINKEDIN_URL = "https://www.linkedin.com/company/comparare";
const SLIM_HEIGHT = 40; // px

const SiteFooter = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isSplashscreen = location.pathname === "/" && new URLSearchParams(location.search).get("preview") !== "true";
  const { user, loading, adminLoading, profileLoading } = useAuth();
  const authSettled = !loading && !profileLoading && (!user || !adminLoading);
  const { data: company } = useCompanyInfo();
  const hasCustomLogo = !!company?.logo_url;
  const isAuthed = !!user;

  const sitemap = [
    { label: "Contact Us", to: isAuthed ? "/user/contact" : "/contact" },
    { label: "FAQ", to: isAuthed ? "/user/faq" : "/faq" },
    { label: "Careers", to: isAuthed ? "/user/careers" : "/careers" },
  ];

  const legal = [
    { label: "Terms & Conditions", to: isAuthed ? "/user/terms-and-conditions" : "/terms-and-conditions" },
    { label: "Privacy Policy", to: isAuthed ? "/user/privacy-policy" : "/privacy-policy" },
    { label: "Cookie Policy", to: isAuthed ? "/user/cookie-policy" : "/cookie-policy" },
  ];

  const largeFooterRef = useRef<HTMLElement | null>(null);
  const [largeHeight, setLargeHeight] = useState<number | null>(null);
  const [revealProgress, setRevealProgress] = useState(0); // 0 = slim visible, 1 = fully melted away
  const [ready, setReady] = useState(false);

  // Measure the large footer height so the spacer matches it exactly
  useEffect(() => {
    if (isAdmin || isSplashscreen || !authSettled) {
      setReady(false);
      setLargeHeight(null);
      return;
    }
    const el = largeFooterRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setLargeHeight(el.offsetHeight);
    });
    ro.observe(el);
    setLargeHeight(el.offsetHeight);
    // Allow layout to settle, then reveal the footer to avoid a flash on load
    const t = window.setTimeout(() => setReady(true), 50);
    return () => {
      ro.disconnect();
      window.clearTimeout(t);
    };
  }, [isAdmin, isSplashscreen, authSettled]);

  // Drive the slim footer fade based on how much of the large footer is visible
  useEffect(() => {
    if (isAdmin || isSplashscreen || !authSettled || largeHeight == null) return;
    let frame = 0;
    const compute = () => {
      const doc = document.documentElement;
      const scrollBottom = window.scrollY + window.innerHeight;
      const distanceFromBottom = doc.scrollHeight - scrollBottom; // px of large footer still hidden
      const revealed = Math.max(0, largeHeight - distanceFromBottom);
      const fadeRange = 120;
      const p = Math.min(1, revealed / fadeRange);
      setRevealProgress(p);
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        compute();
      });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isAdmin, isSplashscreen, authSettled, largeHeight]);

  if (isAdmin || isSplashscreen || !authSettled) return null;

  const scrollToTop = () => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
  };

  const logoOpacity = 1 - revealProgress;
  const logoBlur = revealProgress * 4;
  const spacerHeight = largeHeight ?? 0;

  return (
    <>
      {/* In-flow reveal spacer: occupies the same height as the large footer so the
          page can scroll up and uncover the fixed large footer beneath it. */}
      <div aria-hidden="true" style={{ height: spacerHeight }} />

      {/* Parallax large footer: fixed to bottom of viewport, behind page content. */}
      <footer
        ref={largeFooterRef}
        className="fixed inset-x-0 bottom-0 z-0 bg-secondary text-cream print:hidden"
        style={{
          opacity: ready ? 1 : 0,
          visibility: ready ? "visible" : "hidden",
          transition: "opacity 200ms ease",
        }}
      >
        <div className="px-6 pb-14 pt-8 sm:pb-16 sm:pt-9">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
              <img src={logoCream.url} alt="Comparare" className="mb-4 h-10 w-auto" />
              <p className="max-w-xs text-sm leading-relaxed text-cream/80 text-center sm:text-left">
                The intelligent platform comparison tool for financial advisers.
              </p>
            </div>

            <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
              <h3 className="mb-3 font-heading text-sm uppercase text-cream text-center sm:text-left">Explore</h3>
              <ul className="space-y-2 list-none p-0">
                {sitemap.map((l) => (
                  <li key={l.label} className="text-center sm:text-left">
                    <Link to={l.to} className="text-sm text-cream/80 transition-colors hover:text-cream">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
              <h3 className="mb-3 font-heading text-sm uppercase text-cream text-center sm:text-left">Legal</h3>
              <ul className="space-y-2 list-none p-0">
                {legal.map((l) => (
                  <li key={l.label} className="text-center sm:text-left">
                    <Link to={l.to} className="text-sm text-cream/80 transition-colors hover:text-cream">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
              <h3 className="mb-3 font-heading text-sm uppercase text-cream text-center sm:text-left">Connect</h3>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Comparare on LinkedIn"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/30 text-cream transition-colors hover:bg-cream hover:text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-cream"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 flex justify-center" style={{ bottom: SLIM_HEIGHT + 5 }}>
          <div className="h-px w-[90%] bg-cream/40" />
        </div>
      </footer>

      {/* Sticky slim footer: pinned to viewport bottom in front of page content.
          Fades and softens away as the large footer is revealed by scrolling. */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 bg-secondary text-cream print:hidden"
        style={{ height: SLIM_HEIGHT }}
      >
        <div className="mx-auto grid h-full max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 sm:px-6">
          <div
            className="flex min-w-0 items-center gap-2"
            style={{
              opacity: logoOpacity,
              filter: logoBlur ? `blur(${logoBlur}px)` : undefined,
              transition: "opacity 120ms linear, filter 120ms linear",
            }}
            aria-hidden={revealProgress > 0.9}
          >
            {hasCustomLogo && (
              <span className="hidden font-heading text-[11px] font-medium text-orange-300 sm:inline">
                powered by
              </span>
            )}
            <img
              src={logoCream.url}
              alt="Comparare"
              className="h-5 w-auto max-w-[7.5rem]"
            />
          </div>

          <button
            type="button"
            onClick={scrollToTop}
            aria-label="Back to top"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-cream/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cream"
          >
            <ChevronUp className="h-4 w-4" />
          </button>

          <p className="min-w-0 truncate text-right text-[11px] text-cream/80 sm:text-sm">
            <span className="hidden sm:inline">© Comparare 2026. All rights reserved.</span>
            <span className="sm:hidden">© 2026</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default SiteFooter;
